const MAX_ORIGINS = 50

export interface RouteReference {
  key: string
  pathname: string
  search: string
  hash?: string
}

export interface BillListOriginState {
  listEntryKey: string
  listPathname: string
  listSearch: string
  targetBillId: string
}

export interface BillDetailOriginRef {
  billOriginRef: string
  targetBillId: string
}

export interface BillDetailVisitRef {
  billDetailVisitRef: string
  targetBillId: string
}

interface BillDetailOrigin {
  source: RouteReference
  targetBillId: string
  kind: 'bill-list' | 'in-app'
}

interface BillListReturnCandidate extends BillListOriginState {
  scrollY: number
  restoration: 'armed' | 'cancelled' | 'consumed'
}

interface BillDetailVisit {
  originRef: string
  targetBillId: string
  entryDepths: Map<string, number>
}

const candidates = new Map<string, BillListReturnCandidate>()
const detailOrigins = new Map<string, BillDetailOrigin>()
const detailVisits = new Map<string, BillDetailVisit>()
const detailEntryVisits = new Map<string, string>()
let nextVisitId = 1

function boundMap<T>(map: Map<string, T>) {
  while (map.size > MAX_ORIGINS) {
    const firstKey = map.keys().next().value
    if (firstKey === undefined) return
    map.delete(firstKey)
  }
}

function asOriginRef(state: unknown): BillDetailOriginRef | null {
  const origin = state as Partial<BillDetailOriginRef> | null
  if (!origin || typeof origin.billOriginRef !== 'string' || typeof origin.targetBillId !== 'string') return null
  const remembered = detailOrigins.get(origin.billOriginRef)
  if (!remembered || remembered.targetBillId !== origin.targetBillId) return null
  return { billOriginRef: origin.billOriginRef, targetBillId: origin.targetBillId }
}

function asVisitRef(state: unknown): BillDetailVisitRef | null {
  const visit = state as Partial<BillDetailVisitRef> | null
  if (!visit || typeof visit.billDetailVisitRef !== 'string' || typeof visit.targetBillId !== 'string') return null
  const remembered = detailVisits.get(visit.billDetailVisitRef)
  if (!remembered || remembered.targetBillId !== visit.targetBillId) return null
  return { billDetailVisitRef: visit.billDetailVisitRef, targetBillId: visit.targetBillId }
}

export function isPlainPrimaryActivation(event: Pick<MouseEvent, 'button' | 'altKey' | 'ctrlKey' | 'metaKey' | 'shiftKey'>) {
  return event.button === 0 && !event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey
}

export function makeBillListOriginRef(listEntryKey: string, targetBillId: string): BillDetailOriginRef {
  return { billOriginRef: `bill-list:${listEntryKey}:${targetBillId}`, targetBillId }
}

export function captureBillListReturn(state: BillListOriginState, scrollY = window.scrollY) {
  candidates.set(state.listEntryKey, { ...state, scrollY, restoration: 'armed' })
  const origin = makeBillListOriginRef(state.listEntryKey, state.targetBillId)
  detailOrigins.set(origin.billOriginRef, {
    source: { key: state.listEntryKey, pathname: state.listPathname, search: state.listSearch },
    targetBillId: state.targetBillId,
    kind: 'bill-list',
  })
  boundMap(candidates)
  boundMap(detailOrigins)
}

export function captureInAppBillOrigin(source: RouteReference, targetBillId: string): BillDetailOriginRef {
  const ref = `in-app:${source.key}:${targetBillId}`
  detailOrigins.set(ref, { source: { ...source }, targetBillId, kind: 'in-app' })
  boundMap(detailOrigins)
  return { billOriginRef: ref, targetBillId }
}

export function hasValidBillListOrigin(state: unknown) {
  const ref = asOriginRef(state)
  return Boolean(ref && detailOrigins.get(ref.billOriginRef)?.kind === 'bill-list')
}

export function hasValidInAppBillOrigin(state: unknown) {
  const ref = asOriginRef(state)
  return Boolean(ref && detailOrigins.get(ref.billOriginRef)?.kind === 'in-app')
}

export function beginBillDetailVisit(entryKey: string, targetBillId: string, state: unknown): BillDetailVisitRef | null {
  const rememberedVisit = detailEntryVisits.get(entryKey)
  if (rememberedVisit) {
    const visit = detailVisits.get(rememberedVisit)
    if (visit?.targetBillId === targetBillId) return { billDetailVisitRef: rememberedVisit, targetBillId }
  }
  const stateVisit = asVisitRef(state)
  if (stateVisit?.targetBillId === targetBillId) return stateVisit
  const origin = asOriginRef(state)
  if (!origin || origin.targetBillId !== targetBillId) return null
  const visitRef = `bill-detail-visit:${nextVisitId++}`
  detailVisits.set(visitRef, { originRef: origin.billOriginRef, targetBillId, entryDepths: new Map([[entryKey, 0]]) })
  detailEntryVisits.set(entryKey, visitRef)
  boundMap(detailVisits)
  boundMap(detailEntryVisits)
  return { billDetailVisitRef: visitRef, targetBillId }
}

export function recordBillDetailVisitEntry(ref: BillDetailVisitRef, entryKey: string, depth: number) {
  const visit = detailVisits.get(ref.billDetailVisitRef)
  if (!visit || visit.targetBillId !== ref.targetBillId) return
  visit.entryDepths.set(entryKey, Math.max(0, depth))
  detailEntryVisits.set(entryKey, ref.billDetailVisitRef)
  boundMap(detailEntryVisits)
}

export function getBillDetailVisitDepth(ref: BillDetailVisitRef | null, entryKey: string) {
  if (!ref) return null
  const visit = detailVisits.get(ref.billDetailVisitRef)
  return visit?.targetBillId === ref.targetBillId ? visit.entryDepths.get(entryKey) ?? null : null
}

export function getBillDetailVisitOrigin(ref: BillDetailVisitRef | null) {
  if (!ref) return null
  const visit = detailVisits.get(ref.billDetailVisitRef)
  if (!visit || visit.targetBillId !== ref.targetBillId) return null
  return detailOrigins.get(visit.originRef) ?? null
}

export function armBillListReturnForVisit(ref: BillDetailVisitRef | null) {
  const origin = getBillDetailVisitOrigin(ref)
  if (origin?.kind !== 'bill-list') return
  const candidate = candidates.get(origin.source.key)
  if (candidate && candidate.targetBillId === origin.targetBillId) candidate.restoration = 'armed'
}

export function billListReturnIsPending(entryKey: string, pathname: string, search: string) {
  return getBillListReturnCandidate(entryKey, pathname, search) !== null
}

export function getBillListReturnCandidate(entryKey: string, pathname: string, search: string) {
  const candidate = candidates.get(entryKey)
  if (!candidate || candidate.restoration !== 'armed') return null
  if (candidate.listPathname !== pathname || candidate.listSearch !== search) return null
  return candidate
}

export function cancelBillListReturn(entryKey: string) {
  const candidate = candidates.get(entryKey)
  if (candidate?.restoration === 'armed') candidate.restoration = 'cancelled'
}

export function consumeBillListReturn(entryKey: string) {
  const candidate = candidates.get(entryKey)
  if (candidate?.restoration === 'armed') candidate.restoration = 'consumed'
}

export function refreshConsumedBillListSnapshot(entryKey: string, pathname: string, search: string, scrollY: number) {
  const candidate = candidates.get(entryKey)
  if (!candidate || candidate.restoration !== 'consumed') return
  if (candidate.listPathname !== pathname || candidate.listSearch !== search) return
  if (!Number.isFinite(scrollY) || scrollY < 0) return
  candidate.scrollY = scrollY
}

export function clearBillListReturnMemory() {
  candidates.clear()
  detailOrigins.clear()
  detailVisits.clear()
  detailEntryVisits.clear()
  nextVisitId = 1
}
